import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { PlayerJornadaReto, RetoSender } from "../lib/api";
import { jornadaWindowParts } from "../lib/jornadaDates";
import { retoOwnSide, retoReportHref, retoRival, retoShowsContact } from "../lib/retoContacto";
import { routes } from "../routes";
import { scoreTextFromWinner } from "../lib/scoreFromWinner";
import PlayerRetoActions from "./PlayerRetoActions";

export type JornadaRetoState =
  | { kind: "result"; score: string; won: boolean | null }
  | { kind: "reported"; score: string; won: boolean | null }
  | { kind: "open" };

/** Qué dice el partido de esa jornada: su resultado (desde quien ganó), su
 * reporte esperando al admin o nada aún (se puede reportar). */
export function jornadaRetoState(entry: PlayerJornadaReto, ownParticipantId: number | null): JornadaRetoState {
  const match = entry.match;
  if (!match) return { kind: "open" };
  const p1 = match.player1?.participant_id ?? null;
  const fromWinner = (score: string | null, winner: number | null) =>
    scoreTextFromWinner(score, winner == null || p1 == null ? null : winner === p1).trim();
  const wonBy = (winner: number | null) => (winner == null || ownParticipantId == null ? null : winner === ownParticipantId);
  if (entry.has_result) {
    return {
      kind: "result",
      score: fromWinner(match.score_text, match.winner_participant_id),
      won: wonBy(match.winner_participant_id),
    };
  }
  if (entry.my_report) {
    return {
      kind: "reported",
      score: fromWinner(entry.my_report.score_text, entry.my_report.winner_participant_id),
      won: wonBy(entry.my_report.winner_participant_id),
    };
  }
  return { kind: "open" };
}

/** Píldora corta del estado del partido de la jornada (Juegos, 2026-10-06). */
export function jornadaRetoStatusLabel(state: JornadaRetoState): string {
  if (state.kind === "result") return "Jugado";
  if (state.kind === "reported") return "Reportado";
  return "Por jugar";
}

function capitalize(text: string): string {
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
}

interface PlayerJornadaRetoCardProps {
  entry: PlayerJornadaReto;
  /** Participante del jugador (o del perfil familiar elegido). */
  ownParticipantId: number | null;
  /** Quien escribe el WhatsApp al rival (perfil propio, estructurado). */
  sender?: RetoSender | null;
  /** Perfil con que se reporta (cuenta familiar). */
  playerProfileId?: number | null;
  tournamentName?: string | null;
  headingId: string;
  eyebrow?: string;
  /** Píldora de la derecha; sin ella, la jornada («Jornada 2»). */
  pill?: ReactNode;
  /** Selector «Jornada 1 2 3…» del inicio del torneo. */
  picker?: ReactNode;
  /** Fechas de la jornada debajo del rival (Juegos ya las dice arriba). */
  showDates?: boolean;
  className?: string;
}

/** Tarjeta azul del partido de UNA jornada (round robin sin calendario de
 * canchas): rival, su resultado o tu reporte, o WhatsApp / Llamar /
 * «Reportar resultado». La comparten el inicio del torneo (jornada elegida)
 * y Juegos («Tu partido»). */
export default function PlayerJornadaRetoCard({
  entry,
  ownParticipantId,
  sender,
  playerProfileId,
  tournamentName,
  headingId,
  eyebrow = "Tu reto",
  pill,
  picker,
  showDates = true,
  className = "",
}: PlayerJornadaRetoCardProps) {
  const match = entry.match;
  if (!match) return null;
  const parts = showDates ? jornadaWindowParts(entry.round_starts_on, entry.round_ends_on) : null;
  const ownIds = [ownParticipantId];
  const rival = retoRival(match, { ownParticipantIds: ownIds });
  const ownSide = retoOwnSide(match, { ownParticipantIds: ownIds });
  const state = jornadaRetoState(entry, ownSide?.participant_id ?? ownParticipantId);
  const outcome = (won: boolean | null, score: string) =>
    [won === true ? "Ganaste" : won === false ? "Perdiste" : null, score || null].filter(Boolean).join(" ");
  const reportHref = retoReportHref(match.id, playerProfileId);
  return (
    <section
      className={`pt-home__reto pt-home__reto--jornada${className ? ` ${className}` : ""}`}
      aria-labelledby={headingId}
    >
      <div className="pt-home__reto-head">
        <span className="pt-home__eyebrow">{eyebrow}</span>
        <span className="pt-home__pill">{pill ?? entry.round_label}</span>
      </div>
      {picker}
      <div className="pt-home__reto-who">
        <h2 id={headingId} className="pt-home__rival">
          {rival?.player_profile_id ? (
            <Link className="pt-home__rival-link" to={routes.playerGlobalProfile(rival.player_profile_id)}>
              {rival.display_name ? `vs ${rival.display_name}` : "Rival por definir"}
            </Link>
          ) : rival?.display_name ? (
            `vs ${rival.display_name}`
          ) : (
            "Rival por definir"
          )}
        </h2>
        {parts ? (
          <p className="pt-home__dates" data-no-toast data-jornada-window>
            <span>{capitalize(parts.range)}</span>
          </p>
        ) : null}
      </div>
      {state.kind === "result" ? (
        <div className="pt-home__jornada-result" data-no-toast>
          <p className="pt-home__jornada-score">{outcome(state.won, state.score) || "Resultado capturado"}</p>
          <span className="pt-home__jornada-status is-approved">Aprobado</span>
        </div>
      ) : state.kind === "reported" ? (
        <div className="pt-home__jornada-result" data-no-toast>
          <p className="pt-home__jornada-score">Tu reporte: {outcome(state.won, state.score) || "sin marcador"}</p>
          <span className="pt-home__jornada-status is-pending">Reportado, esperando al admin</span>
          {match.allow_player_result_reporting ? (
            <Link className="pt-home__jornada-edit" to={reportHref}>
              Editar mi reporte
            </Link>
          ) : null}
        </div>
      ) : (
        <>
          {entry.rival_reported ? (
            <p className="pt-home__reto-note" data-no-toast>
              Tu rival ya reportó el resultado: repórtalo tú también para confirmarlo.
            </p>
          ) : null}
          {retoShowsContact(match) ? (
            <PlayerRetoActions
              variant="card"
              match={match}
              rival={rival}
              sender={sender}
              tournamentName={tournamentName}
              reportHref={reportHref}
              showContactLine
              className="pt-home__reto-actions"
            />
          ) : null}
        </>
      )}
    </section>
  );
}
